import React, { useEffect, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  updatePassword,
  clearError,
  selectIsUpdatingProfile,
  selectAuthError,
  selectUser,
  selectValidationErrors,
} from './authSlice';
import {
  BrandLogo,
  IconTextField,
  PrimaryCTA,
} from '../../components';
import { fpColors } from '../../styles/theme';
import type { UpdatePasswordRequest } from '../../api/types';

const schema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup
    .string()
    .min(8, 'New password must be at least 8 characters')
    .required('New password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password')], 'Passwords must match')
    .required('Please confirm your new password'),
});

interface ChangePasswordFormData extends UpdatePasswordRequest {
  confirm_password: string;
}

type Strength = {
  label: string;
  level: 'weak' | 'fair' | 'good' | 'strong';
  color: string;
  fill: number; // 0..1
};

const getStrength = (password: string): Strength => {
  if (!password) {
    return { label: '', level: 'weak', color: fpColors.outline, fill: 0 };
  }
  if (password.length < 8) {
    return { label: 'Too short', level: 'weak', color: fpColors.error, fill: 0.25 };
  }
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNum = /\d/.test(password);
  const hasSym = /[^A-Za-z0-9]/.test(password);
  const score = [hasUpper, hasLower, hasNum, hasSym].filter(Boolean).length;
  if (score <= 1) {
    return { label: 'Weak', level: 'weak', color: fpColors.error, fill: 0.35 };
  }
  if (score === 2) {
    return { label: 'Fair', level: 'fair', color: fpColors.tertiary, fill: 0.6 };
  }
  if (score === 3) {
    return { label: 'Good', level: 'good', color: fpColors.primary, fill: 0.8 };
  }
  return { label: 'Strong', level: 'strong', color: fpColors.primary, fill: 1 };
};

/**
 * Change password — Fidelity Purple. Custom top bar, three
 * IconTextFields with eye-toggle, live strength meter, requirements
 * checklist, and a primary CTA. Native stack header disabled.
 */
export const ChangePasswordScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectIsUpdatingProfile);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);
  // Used by the "Forgot it?" escape hatch below — Google-only users
  // (FE-168) and password-forgetters can both promote to email reset
  // without leaving this screen tree.
  const currentUser = useAppSelector(selectUser);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const newPassword = watch('new_password');
  const strength = getStrength(newPassword || '');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: ChangePasswordFormData) => {
    const { confirm_password: _c, ...updateData } = data;
    const result = await dispatch(updatePassword(updateData));
    if (updatePassword.fulfilled.match(result)) {
      reset();
      goBackOrHome(navigation);
    }
  };

  const bannerError =
    (validationErrors && typeof validationErrors === 'string'
      ? validationErrors
      : null) ||
    (typeof error === 'string' ? error : null);

  const requirements = [
    {
      label: 'At least 8 characters',
      met: (newPassword?.length ?? 0) >= 8,
    },
    {
      label: 'Upper & lower case letters',
      met:
        !!newPassword &&
        /[A-Z]/.test(newPassword) &&
        /[a-z]/.test(newPassword),
    },
    {
      label: 'At least one number',
      met: !!newPassword && /\d/.test(newPassword),
    },
  ];

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <View style={styles.topBrand}>
            <BrandLogo variant="horizontal" height={22} tone="color" />
          </View>
          <View style={styles.topBtn} />
        </View>
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.kicker}>SECURITY</Text>
          <Text style={styles.h1}>Change password</Text>
          <Text style={styles.subtitle}>
            You’ll stay signed in here, but other devices will be
            logged out.
          </Text>

          <View style={styles.card}>
            <Controller
              control={control}
              name="current_password"
              render={({ field: { onChange, onBlur, value } }) => (
                <IconTextField
                  label="Current password"
                  leftIcon="lock-outline"
                  rightIcon={showCurrent ? 'visibility' : 'visibility-off'}
                  onRightIconPress={() => setShowCurrent((v) => !v)}
                  placeholder="Enter current password"
                  value={value || ''}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={errors.current_password?.message}
                  secureTextEntry={!showCurrent}
                  autoComplete="current-password"
                  autoFocus
                />
              )}
            />

            {/* FE-168: escape hatch for Google-only users (no
                current password to enter) and anyone who genuinely
                forgot theirs. Hands them to the existing OTP flow
                with their email pre-filled. */}
            <Pressable
              onPress={() =>
                (navigation as any).navigate('ForgotPassword', {
                  email: currentUser?.email,
                })
              }
              style={({ pressed }) => [
                styles.forgotLinkRow,
                pressed && { opacity: 0.6 },
              ]}
              accessibilityRole="link"
              accessibilityLabel="Reset password via email instead"
              testID="change-password-forgot-link"
            >
              <MaterialIcons name="mail-outline" size={14} color={fpColors.primary} />
              <Text style={styles.forgotLinkText}>
                Forgot it? Reset via email instead
              </Text>
            </Pressable>

            <Controller
              control={control}
              name="new_password"
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <IconTextField
                    label="New password"
                    leftIcon="vpn-key"
                    rightIcon={showNew ? 'visibility' : 'visibility-off'}
                    onRightIconPress={() => setShowNew((v) => !v)}
                    placeholder="Enter a new password"
                    value={value || ''}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={errors.new_password?.message}
                    secureTextEntry={!showNew}
                    autoComplete="new-password"
                  />
                  {value ? (
                    <View style={styles.strengthRow}>
                      <View style={styles.strengthBarTrack}>
                        <View
                          style={[
                            styles.strengthBarFill,
                            {
                              width: `${Math.round(strength.fill * 100)}%`,
                              backgroundColor: strength.color,
                            },
                          ]}
                        />
                      </View>
                      <Text style={[styles.strengthLabel, { color: strength.color }]}>
                        {strength.label}
                      </Text>
                    </View>
                  ) : null}
                </>
              )}
            />

            <Controller
              control={control}
              name="confirm_password"
              render={({ field: { onChange, onBlur, value } }) => (
                <IconTextField
                  label="Confirm new password"
                  leftIcon="check-circle-outline"
                  rightIcon={showConfirm ? 'visibility' : 'visibility-off'}
                  onRightIconPress={() => setShowConfirm((v) => !v)}
                  placeholder="Re-enter new password"
                  value={value || ''}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={errors.confirm_password?.message}
                  secureTextEntry={!showConfirm}
                  autoComplete="new-password"
                />
              )}
            />
          </View>

          <View style={styles.reqCard}>
            <Text style={styles.reqHeader}>REQUIREMENTS</Text>
            {requirements.map((r) => (
              <View key={r.label} style={styles.reqRow}>
                <MaterialIcons
                  name={r.met ? 'check-circle' : 'radio-button-unchecked'}
                  size={16}
                  color={r.met ? fpColors.primary : fpColors.outline}
                />
                <Text
                  style={[
                    styles.reqLabel,
                    r.met && { color: fpColors.onSurface },
                  ]}
                >
                  {r.label}
                </Text>
              </View>
            ))}
          </View>

          {bannerError ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={fpColors.error}
              />
              <Text style={styles.errorText}>{bannerError}</Text>
            </View>
          ) : null}

          <PrimaryCTA
            label={isLoading ? 'Updating\u2026' : 'Update password'}
            onPress={handleSubmit(onSubmit)}
            loading={isLoading}
            iconRight={isLoading ? null : 'check-circle'}
          />

          <Text style={styles.securityNote}>
            For your security, updating your password signs you out of
            other devices.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 10,
  },
  topBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 20, gap: 14, paddingBottom: 30 },
  kicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.4,
    marginTop: 2,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    marginBottom: 6,
  },

  card: {
    padding: 14,
    borderRadius: 16,
    gap: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },

  strengthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: -6,
  },
  strengthBarTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: fpColors.surfaceContainerLow,
    overflow: 'hidden',
  },
  strengthBarFill: {
    height: '100%',
    borderRadius: 3,
  },
  strengthLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.3,
    minWidth: 48,
    textAlign: 'right',
  },

  reqCard: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: `${fpColors.primary}08`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}1f`,
    gap: 6,
  },
  reqHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.6,
    marginBottom: 4,
  },
  reqRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  reqLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },

  securityNote: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    textAlign: 'center',
    marginTop: 2,
  },

  forgotLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    marginTop: -4,
    marginBottom: 4,
    paddingVertical: 4,
  },
  forgotLinkText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.primary,
    textDecorationLine: 'underline',
  },
});
